import {WidgetErrorBanner} from 'shells/card/card-widget/widget-error-banner';
import {PhoneRow} from 'shells/row/phone-row/phone-row';

import type {
    SessionUser,
    PeepBusinessCardInfoFragment_phones_all as Phone,
} from 'nutshell-graphql-types';

import type {UserFacingEntity} from '../../../../core-entity/rows/peep-rows/types';

import {getTitleDropdownOptions} from '../helpers';
import type {PatchPhoneInput, PatchTitleInput, RowErrorMessage} from '../types';

interface Props {
    entityId: string;
    phones: Phone[] | null | undefined;
    canMarkAsPrimary: boolean;
    parentEntity: UserFacingEntity;
    labelOptions: string[] | null | undefined;
    user: SessionUser | null | undefined;
    newlyAddedId?: string | null;
    onUpdatePhone: (args: PatchPhoneInput) => Promise<any>;
    onUpdateTitle: (args: PatchTitleInput) => Promise<any>;
    rowErrorMessage: RowErrorMessage | null;
    setRowErrorMessage: (rowErrorMessage: RowErrorMessage | null) => void;
}

export function PhoneRowList(props: Props) {
    const hasConnectedPhoneIntegration = Boolean(
        props.user &&
            (props.user.phoneIntegrations.some(
                (integration) => integration && integration.isEnabledForUser
            ) ||
                (props.user.clickToCallIntegration && props.user.clickToCallIntegration.isEnabled))
    );

    return (
        <>
            {props.phones?.map((phone) => {
                const formattedNumber = phone.value?.numberFormattedWithCountryCode;
                if (!formattedNumber) {
                    return null;
                }
                const errorMessage =
                    props.rowErrorMessage?.id === phone.id
                        ? props.rowErrorMessage.errorMessage
                        : null;

                const dropdownOptions = getTitleDropdownOptions(
                    props.labelOptions,
                    phone.id,
                    props.onUpdateTitle
                );

                return (
                    <div key={`${phone.id}-phone`}>
                        {errorMessage && (
                            <div className='pb-2 px-8'>
                                <WidgetErrorBanner
                                    error={errorMessage}
                                    setError={(error?: string | null) => {
                                        props.setRowErrorMessage(
                                            error
                                                ? {
                                                      id: phone.id,
                                                      errorMessage: error,
                                                  }
                                                : null
                                        );
                                    }}
                                />
                            </div>
                        )}
                        <PhoneRow
                            title={phone.name}
                            parentEntity={props.parentEntity}
                            isPrimary={phone.isPrimary}
                            showIsPrimaryIcon={props.phones?.length !== 1}
                            isFormer={phone.isFormer}
                            canMarkAsPrimary={props.canMarkAsPrimary}
                            formattedNumber={formattedNumber}
                            extension={phone.value?.extension}
                            dropdownOptions={dropdownOptions}
                            onUpdatePhone={(number: string, extension: string) =>
                                props.onUpdatePhone({
                                    attributeValueId: phone.id,
                                    phoneValue: {
                                        countryCodeAndNumber: number,
                                        extension,
                                    },
                                })
                            }
                            onMarkAsPrimary={() =>
                                props.onUpdatePhone({
                                    attributeValueId: phone.id,
                                    isPrimary: true,
                                })
                            }
                            onChangeFormerStatus={(isFormer: boolean) =>
                                props.onUpdatePhone({
                                    attributeValueId: phone.id,
                                    isFormer,
                                })
                            }
                            onDelete={() =>
                                props.onUpdatePhone({
                                    attributeValueId: phone.id,
                                    isDelete: true,
                                })
                            }
                            onUpdateTitle={(title: string) =>
                                props.onUpdateTitle({
                                    attributeValueId: phone.id,
                                    name: title,
                                })
                            }
                            connectButtonsProps={{
                                id: props.entityId,
                                phone: phone.value,
                                phoneCallerType: props.user?.phoneCallerType,
                                userCanUseSms: props.user?.permissions?.canUseSms,
                                sendFromNutshellEnabled: props.user?.sendFromNutshellEnabled,
                                hasConnectedPhoneIntegration,
                                deliverabilityStatus: phone.deliverabilityStatus,
                            }}
                            successFlashKey={props.newlyAddedId === phone.id ? phone.id : undefined}
                        />
                    </div>
                );
            })}
        </>
    );
}
